<script setup>
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { listAppeals, listModerationAudit, listModerationQueue, moderateMap, resolveAppeal } from "@/api/customMapApi.js";
import { useLanguageStore } from "@/stores/languageStore.js";

const router = useRouter();
const languageStore = useLanguageStore();
const status = ref("pending");
const maps = ref([]);
const loading = ref(false);
const error = ref("");
const busyId = ref("");
const reasons = ref({});
const appeals = ref([]);
const auditLogs = ref([]);
const auditLoading = ref(false);
const auditError = ref("");

const auditActionKeys = Object.freeze({
  approve: "auditActionApprove",
  reject: "auditActionReject",
  hide: "auditActionHide",
  role_change: "auditActionRoleChange",
  ban: "auditActionBan",
  unban: "auditActionUnban",
  appeal_accept: "auditActionAppealAccept",
  appeal_reject: "auditActionAppealReject"
});

function auditActionLabel(action) {
  const key = auditActionKeys[action];
  return key ? languageStore.t(key) : String(action || "-");
}

function formatAuditTime(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString(languageStore.htmlLanguage);
}

async function loadAuditLogs() {
  auditLoading.value = true;
  auditError.value = "";
  try {
    const result = await listModerationAudit();
    auditLogs.value = Array.isArray(result?.logs) ? result.logs : [];
  } catch {
    auditLogs.value = [];
    auditError.value = languageStore.t("operationLogLoadFailed");
  } finally {
    auditLoading.value = false;
  }
}

async function loadQueue() {
  loading.value = true;
  error.value = "";
  try {
    const result = await listModerationQueue(status.value);
    maps.value = Array.isArray(result?.maps) ? result.maps : [];
    const appealResult = await listAppeals("pending");
    appeals.value = Array.isArray(appealResult?.appeals) ? appealResult.appeals : [];
  } catch (cause) {
    maps.value = [];
    error.value = cause?.status === 403 ? languageStore.t("moderatorOnly") : (cause?.message || languageStore.t("unavailable"));
  } finally {
    loading.value = false;
  }
}

async function decideAppeal(appeal, decision) {
  busyId.value = `appeal-${appeal.id}`;
  try { await resolveAppeal(appeal.id, decision, ""); appeals.value=appeals.value.filter(item=>item.id!==appeal.id); await loadAuditLogs(); }
  catch(cause){ error.value=cause?.message||languageStore.t("saveFailed"); }
  finally{ busyId.value=""; }
}

async function decide(map, decision) {
  if (busyId.value) return;
  const reason = decision === "approve" ? "" : String(reasons.value[map.id] || "").trim();
  busyId.value = map.id;
  error.value = "";
  try {
    await moderateMap(map.id, decision, reason);
    maps.value = maps.value.filter(item => item.id !== map.id);
    window.dispatchEvent(new CustomEvent("workshop-moderation-changed", { detail: { mapId: map.id, decision } }));
    localStorage.setItem("workshop-moderation-revision", `${Date.now()}:${map.id}:${decision}`);
    await loadAuditLogs();
  } catch (cause) {
    error.value = cause?.message || languageStore.t("saveFailed");
  } finally {
    busyId.value = "";
  }
}

onMounted(() => {
  loadQueue();
  loadAuditLogs();
});
</script>

<template>
  <section class="moderation page-card">
    <header class="page-header">
      <button class="back-button" type="button" @click="router.push({ name: 'home' })">← {{ languageStore.t('back') }}</button>
      <h1 class="page-title">{{ languageStore.t('contentModeration') }}</h1>
      <span />
    </header>
    <div class="filters">
      <select v-model="status" @change="loadQueue">
        <option value="pending">{{ languageStore.t('moderation_pending') }}</option>
        <option value="approved">{{ languageStore.t('moderation_approved') }}</option>
        <option value="rejected">{{ languageStore.t('moderation_rejected') }}</option>
        <option value="hidden">{{ languageStore.t('moderation_hidden') }}</option>
      </select>
      <button type="button" @click="loadQueue">{{ languageStore.t('refresh') }}</button>
    </div>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
    <p v-if="loading" class="empty">{{ languageStore.t('loading') }}</p>
    <p v-else-if="!maps.length && !error" class="empty">{{ languageStore.t('moderationQueueEmpty') }}</p>
    <div class="queue">
      <article v-for="map in maps" :key="map.id" class="review-card">
        <img :src="map.image_url" :alt="map.name" />
        <div class="copy">
          <h2>{{ map.name }}</h2>
          <p>{{ map.description }}</p>
          <small>{{ map.author }} · {{ languageStore.t('reportCount', { count: map.report_count || 0 }) }}</small>
          <em v-if="map.moderation_reason">{{ map.moderation_reason }}</em>
          <input v-model="reasons[map.id]" class="reason-input" type="text" maxlength="300" :placeholder="languageStore.t('moderationReasonPrompt')" />
        </div>
        <div class="actions">
          <button class="approve" type="button" :disabled="busyId === map.id" @click="decide(map, 'approve')">{{ languageStore.t('approve') }}</button>
          <button class="reject" type="button" :disabled="busyId === map.id" @click="decide(map, 'reject')">{{ languageStore.t('reject') }}</button>
          <button type="button" :disabled="busyId === map.id" @click="decide(map, 'hide')">{{ languageStore.t('hide') }}</button>
        </div>
      </article>
    </div>
    <h2 class="appeal-title">{{ languageStore.t('pendingAppeals') }}</h2>
    <p v-if="!appeals.length" class="empty">{{ languageStore.t('moderationQueueEmpty') }}</p>
    <div class="queue"><article v-for="appeal in appeals" :key="appeal.id" class="review-card"><img :src="appeal.image_url" :alt="appeal.map_name"/><div class="copy"><h2>{{ appeal.map_name }}</h2><p>{{ appeal.message }}</p><small>{{ appeal.author }}</small></div><div class="actions"><button class="approve" @click="decideAppeal(appeal,'accept')">{{ languageStore.t('approve') }}</button><button class="reject" @click="decideAppeal(appeal,'reject')">{{ languageStore.t('reject') }}</button></div></article></div>
    <section class="audit-section">
      <div class="audit-header">
        <h2>{{ languageStore.t('operationLog') }}</h2>
        <button type="button" :disabled="auditLoading" @click="loadAuditLogs">{{ languageStore.t('refresh') }}</button>
      </div>
      <p v-if="auditError" class="error audit-message" role="alert">{{ auditError }}</p>
      <p v-else-if="auditLoading" class="empty audit-message">{{ languageStore.t('loading') }}</p>
      <p v-else-if="!auditLogs.length" class="empty audit-message">{{ languageStore.t('operationLogEmpty') }}</p>
      <div v-else class="audit-list">
        <article v-for="log in auditLogs" :key="log.id" class="audit-item">
          <div class="audit-summary">
            <strong>{{ log.moderator_name || log.moderator_id }}</strong>
            <span>{{ auditActionLabel(log.action) }}</span>
          </div>
          <p v-if="log.map_name"><b>{{ languageStore.t('operationTarget') }}：</b>{{ log.map_name }}</p>
          <p v-if="log.reason"><b>{{ languageStore.t('operationReason') }}：</b>{{ log.reason }}</p>
          <time :datetime="log.created_at">{{ formatAuditTime(log.created_at) }}</time>
        </article>
      </div>
    </section>
  </section>
</template>

<style scoped>
.moderation { min-height:calc(100dvh - 48px); padding:20px; }
.page-header { display:grid; grid-template-columns:1fr auto 1fr; align-items:center; }
.filters { display:flex; gap:10px; margin:16px 0; }
.filters select,.filters button { min-height:40px; border:1px solid rgba(82,246,232,.3); border-radius:10px; padding:0 12px; color:#fff; background:#061a29; }
.queue { display:grid; gap:12px; }
.appeal-title{margin:28px 0 12px;color:#eafffd;font-size:20px}
.audit-section { margin-top:30px; padding-top:22px; border-top:1px solid rgba(82,246,232,.2); }
.audit-header { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:12px; }
.audit-header h2 { margin:0; color:#eafffd; font-size:20px; }
.audit-header button { min-height:36px; border:1px solid rgba(82,246,232,.3); border-radius:9px; padding:0 14px; color:#fff; background:#0b3041; }
.audit-list { display:grid; gap:10px; }
.audit-item { padding:13px 15px; border:1px solid rgba(82,246,232,.18); border-radius:12px; background:rgba(6,26,41,.8); }
.audit-summary { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.audit-summary strong { color:#8ffdf3; }
.audit-summary span { padding:3px 8px; border-radius:999px; color:#fff; background:#15475a; font-size:12px; }
.audit-item p { margin:8px 0 0; color:rgba(255,255,255,.72); overflow-wrap:anywhere; }
.audit-item time { display:block; margin-top:8px; color:rgba(255,255,255,.45); font-size:12px; }
.audit-message { padding:18px; }
.review-card { display:grid; grid-template-columns:150px 1fr auto; gap:14px; padding:14px; border:1px solid rgba(82,246,232,.2); border-radius:16px; background:#061a29; }
.review-card img { width:150px; height:110px; border-radius:10px; object-fit:cover; background:#020912; }
.copy { min-width:0; }
.copy h2 { margin:0 0 5px; font-size:17px; }
.copy p { margin:0 0 8px; color:rgba(255,255,255,.65); }
.copy small,.copy em { display:block; margin-top:5px; color:#8ffdf3; font-size:11px; }
.reason-input { width:100%; min-height:34px; margin-top:9px; border:1px solid rgba(82,246,232,.2); border-radius:8px; padding:0 9px; color:#fff; background:#03131f; }
.actions { display:grid; align-content:center; gap:7px; }
.actions button { min-width:76px; min-height:34px; border:1px solid rgba(255,255,255,.2); border-radius:9px; color:#fff; background:#14283a; }
.actions .approve { border-color:#44dda0; background:#12603f; }
.actions .reject { border-color:#ff718b; background:#642239; }
.empty,.error { padding:30px; text-align:center; color:rgba(255,255,255,.65); }
.error { color:#ff9aad; }
@media (max-width:650px) { .review-card { grid-template-columns:90px 1fr; } .review-card img { width:90px; height:90px; } .actions { grid-column:1/-1; grid-template-columns:repeat(3,1fr); } }
</style>
